import { useCallback, useEffect, useRef, useState } from 'react'

import API from '../../component/Axios'
import { createListLinkTarget } from '../../utils/listPageState'
import {
    clearAppNoticeDraft,
    createAppNoticeDraftPayload,
    saveAppNoticeDraft,
} from './appNoticeDraftUtils'
import {
    APP_NOTICE_IMAGE_LEAVE_MESSAGE,
    hasPendingAppNoticeImages,
    usePendingAppNoticeImageUnloadGuard,
} from './appNoticeLeaveGuard'
import {
    APP_NOTICE_IMAGE_FILE_ERROR_MESSAGE,
    APP_NOTICE_IMAGE_PREVIEW_ERROR_MESSAGE,
    createAppNoticeFormData,
    createNormalizedAppNoticeParams,
    getAppNoticeValidationMessage,
    isAppNoticeImageFile,
    readAppNoticeImagePreview,
} from './appNoticeFormUtils'

// App 공지사항 폼의 draft, 이탈 경고, 입력 변경, 이미지 업로드, 저장 흐름을 한 곳에서 관리한다.
export default function useAppNoticeFormFlow({
    draftKey,
    draftEnabled,
    params,
    push,
    saving,
    setParams,
    setPush,
    setImage,
    setPopupImage,
    setSaving,
    history,
    listState,
    saveConfig,
    showError,
}) {
    const {
        search: listSearch,
        curPage: listCurPage,
        searchText: listSearchText,
        searchSelect: listSearchSelect,
    } = listState
    const {
        endpoint,
        noticeId,
        fallbackErrorMessage,
    } = saveConfig
    const initialDraftSnapshotRef = useRef('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = hasPendingAppNoticeImages(params) && !saving && !navigationUnlocked

    usePendingAppNoticeImageUnloadGuard(leaveGuardEnabled)

    const setInitialDraftSnapshot = useCallback(({
        params: initialParams = {},
        push: initialPush = 0,
    } = {}) => {
        initialDraftSnapshotRef.current = JSON.stringify(createAppNoticeDraftPayload({
            params: initialParams,
            push: initialPush,
        }))
    }, [])

    useEffect(() => {
        if (!draftEnabled || !initialDraftSnapshotRef.current) {
            return
        }

        const nextDraft = createAppNoticeDraftPayload({
            params,
            push,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppNoticeDraft(draftKey)
            return
        }

        saveAppNoticeDraft(draftKey, nextDraft)
    }, [draftEnabled, draftKey, params, push])

    const moveToList = useCallback((snackbarState) => {
        setNavigationUnlocked(true)
        clearAppNoticeDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/appNotices',
                search: listSearch,
                curPage: listCurPage,
                searchText: listSearchText,
                searchSelect: listSearchSelect,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
            }))
        }, 0)
    }, [draftKey, history, listCurPage, listSearch, listSearchSelect, listSearchText])

    const handleBackClick = useCallback((event) => {
        if (!leaveGuardEnabled) {
            clearAppNoticeDraft(draftKey)
            return
        }

        event.preventDefault()

        if (!window.confirm(APP_NOTICE_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }, [draftKey, leaveGuardEnabled, moveToList])

    const handleUpdateField = useCallback((event) => {
        const { name, value } = event.target

        setParams((prev) => ({
            ...prev,
            [name]: value,
        }))
    }, [setParams])

    const handleUpdateDateField = useCallback((name, value) => {
        setParams((prev) => ({
            ...prev,
            [name]: value,
        }))
    }, [setParams])

    const handlePushChange = useCallback((event) => {
        setPush(event.target.value)
    }, [setPush])

    const handleUploadClick = useCallback(async (event, type) => {
        event.preventDefault()

        const file = event.target.files?.[0]
        const fieldName = event.target.name
        if (!file) {
            return
        }

        if (!isAppNoticeImageFile(file)) {
            event.target.value = ''
            showError(APP_NOTICE_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        try {
            const nextImage = await readAppNoticeImagePreview(file)
            setParams((prev) => ({
                ...prev,
                [fieldName]: file,
            }))

            if (type === 1) {
                setPopupImage(nextImage)
            } else if (type === 2) {
                setImage(nextImage)
            }
        } catch (_error) {
            event.target.value = ''
            showError(APP_NOTICE_IMAGE_PREVIEW_ERROR_MESSAGE)
        }
    }, [setImage, setParams, setPopupImage, showError])

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault()

        if (saving) {
            return
        }

        const nextParams = createNormalizedAppNoticeParams(params)
        setParams((prev) => ({
            ...prev,
            title: nextParams.title,
            content: nextParams.content,
        }))

        const validationMessage = getAppNoticeValidationMessage(nextParams)
        if (validationMessage) {
            showError(validationMessage)
            return
        }

        try {
            setSaving(true)

            const formData = createAppNoticeFormData(nextParams, {
                noticeId,
                push,
            })
            const { data } = await API({
                method: 'post',
                url: endpoint,
                data: formData,
                headers: { "Content-Type": "multipart/form-data" },
            })

            if (data.code === 200) {
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                showError(data.message)
            }
        } catch (_error) {
            showError(fallbackErrorMessage)
        } finally {
            setSaving(false)
        }
    }, [
        endpoint,
        fallbackErrorMessage,
        history,
        moveToList,
        noticeId,
        params,
        push,
        saving,
        setParams,
        setSaving,
        showError,
    ])

    return {
        setInitialDraftSnapshot,
        leaveGuardEnabled,
        moveToList,
        handlers: {
            onSubmit: handleSubmit,
            onBackClick: handleBackClick,
            onFieldChange: handleUpdateField,
            onDateChange: handleUpdateDateField,
            onPushChange: handlePushChange,
            onUploadClick: handleUploadClick,
        },
    }
}
